import { Component, HostListener, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { Subject, Observable, forkJoin, of } from 'rxjs';
import { takeUntil, map, catchError } from 'rxjs/operators';
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
  ZoomIcon,
  Maximize01Icon,
  Menu01Icon,
} from '@hugeicons/core-free-icons';
import { GET_ALL_ARTICLES } from '../../../store/entities/epaper-management/epaper.graphql';
import { READ_ATTACHMENT_BY_PATH } from '../../../store/entities/shared/attachment.graphql';

interface Article {
  id: number;
  uuid: string;
  headline: string;
  content: string;
  author: string;
  articleStatus: string;
  publicationDate: string;
  ordering: number;
  imageUrl?: string | null;
  thumbnail?: string;
}

interface MagazinePage {
  pageNumber: number;
  article?: Article;
  isDoublePage?: boolean;
  leftArticle?: Article;
  rightArticle?: Article;
}

@Component({
  selector: 'app-article-by-gazeti',
  templateUrl: './article-by-gazeti.component.html',
  styleUrl: './article-by-gazeti.component.scss'
})
export class ArticleByGazetiComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private route = inject(ActivatedRoute);
   router = inject(Router);
  private apollo = inject(Apollo);

  icons = {
    arrowLeft: ArrowLeft01Icon,
    arrowRight: ArrowRight01Icon,
    close: Cancel01Icon,
    zoomIn: ZoomIcon,
    zoomOut: ZoomIcon,
    fullscreen: Maximize01Icon,
    menu: Menu01Icon,
  };

  gazetiUuid: string = '';
  articles: Article[] = [];
  pages: MagazinePage[] = [];
  currentPageIndex: number = 0;
  isLoading: boolean = true;
  error: string = '';
  isAuthError: boolean = false;

  // View controls
  zoomLevel: number = 1;
  isFullscreen: boolean = false;
  showThumbnails: boolean = false;
  isFlipping: boolean = false;
  flipDirection: 'left' | 'right' = 'right';

  // Animation states
  leftPageRotation: number = 0;
  rightPageRotation: number = 0;

  ngOnInit() {
    this.gazetiUuid = this.route.snapshot.paramMap.get('uuid') || '';
    
    console.log('🔍 Gazeti UUID from route:', this.gazetiUuid);
    
    if (!this.gazetiUuid) {
      this.error = 'Invalid Gazeti ID';
      this.isLoading = false;
      return;
    }

   
    this.checkAuthenticationAndLoadArticles();
  }

  checkAuthenticationAndLoadArticles() {

    const token = this.getAuthToken();
    
    if (!token) {
      console.warn('⚠️ No authentication token found');
      this.redirectToLogin();
      return;
    }

    console.log('✅ User is authenticated, loading articles...');
    this.loadArticles();
  }

  getAuthToken(): string | null {
    // Check multiple possible locations for auth token
    // Adjust these based on your app's authentication implementation
    
    // Option 1: Check localStorage
    const localToken = localStorage.getItem('token') || 
                      localStorage.getItem('authToken') || 
                      localStorage.getItem('access_token') ||
                      localStorage.getItem('auth_token');
    
    // Option 2: Check sessionStorage
    const sessionToken = sessionStorage.getItem('token') ||
                        sessionStorage.getItem('authToken') ||
                        sessionStorage.getItem('access_token');
    
    // Option 3: Check cookies (if your app uses cookies)
    const cookieToken = this.getCookie('token') || 
                       this.getCookie('authToken');

    return localToken || sessionToken || cookieToken;
  }

  getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  redirectToLogin() {
    console.log('🔐 Redirecting to login page...');
    this.isLoading = false;
    this.isAuthError = true;
    this.error = 'Please login to view this content';
    
    // Store the intended destination to return after login
    const returnUrl = `/gazeti-magazine/${this.gazetiUuid}`;
    localStorage.setItem('returnUrl', returnUrl);
    
    // Redirect to login after a brief delay to show the message
    setTimeout(() => {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: returnUrl }
      });
    }, 2000);
  }

  loadArticles() {
    this.isLoading = true;
    this.error = '';

    console.log('📡 Making GraphQL query with variables:', {
      pageParam: { page: 0, size: 100 },
      filters: { pageId: this.gazetiUuid }
    });

    this.apollo
      .query<any>({
        query: GET_ALL_ARTICLES,
        variables: {
          pageParam: {
            page: 0,
            size: 100,
          },
          filters: {
            pageId: this.gazetiUuid,
          }
        },
        fetchPolicy: 'network-only',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          console.log('📥 GraphQL response:', result);

          // Check for authentication error in response
          if (result.data?.getAllArticles?.response?.status === false) {
            const message = result.data.getAllArticles.response.message;
            const code = result.data.getAllArticles.response.code;
            
            console.error('❌ API Error:', message, 'Code:', code);
            
            // Check if it's an authentication error
            if (code === 9003 || message?.toLowerCase().includes('login') || message?.toLowerCase().includes('access denied')) {
              console.warn('🔐 Authentication required');
              this.redirectToLogin();
              return;
            }
            
            // Other API errors
            this.error = message || 'Failed to load articles';
            this.isLoading = false;
            return;
          }
          
          if (result.data?.getAllArticles?.data) {
            const publishedArticles: Article[] = result.data.getAllArticles.data
              .filter((article: Article) => article.articleStatus === 'PUBLISHED')
              .sort((a: Article, b: Article) => a.ordering - b.ordering);

            console.log('✅ Total articles received:', result.data.getAllArticles.data.length);
            console.log('✅ Published articles:', publishedArticles.length);

            // If no articles, show message
            if (publishedArticles.length === 0) {
              this.error = 'No published articles found for this publication';
              this.articles = [];
              this.createPages();
              this.isLoading = false;
              return;
            }

            // Load article images in parallel
            const imageObservables = publishedArticles.map((article: Article) => {
              if (article.thumbnail) {
                return this.loadAttachment(article.thumbnail).pipe(
                  map((imageUrl): Article => ({
                    ...article,
                    imageUrl
                  }))
                );
              }
              return of<Article>({
                ...article,
                imageUrl: null
              });
            });

            forkJoin(imageObservables as Array<Observable<Article>>).subscribe({
              next: (articlesWithImages: Article[]) => {
                this.articles = articlesWithImages;
                this.createPages();
                this.isLoading = false;
                console.log('✅ Loaded articles with images:', this.articles);
              },
              error: (error) => {
                console.error('Error loading article images:', error);
                // Still show articles even if images fail
                this.articles = publishedArticles.map(article => ({
                  ...article,
                  imageUrl: null
                }));
                this.createPages();
                this.isLoading = false;
              }
            });
          } else {
            this.error = 'No articles found for this publication';
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error('❌ GraphQL Error:', error);
          
          // Check if it's an authentication error
          if (error.message?.toLowerCase().includes('unauthorized') || 
              error.message?.toLowerCase().includes('unauthenticated') ||
              error.message?.toLowerCase().includes('login')) {
            console.warn('🔐 Authentication error detected');
            this.redirectToLogin();
            return;
          }
          
          this.error = 'Failed to load articles. Please try again.';
          this.isLoading = false;
        },
      });
  }

  loadAttachment(filePath: string): Observable<string | null> {
    return this.apollo
      .query({
        query: READ_ATTACHMENT_BY_PATH,
        variables: {
          input: {
            filePath: filePath,
          },
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res: any) => {
          const base64Data = res?.data?.getAttachment?.data?.filePath;
          if (base64Data) {
            return `data:image/png;base64,${base64Data}`;
          }
          return null;
        }),
        catchError((error) => {
          console.error('Error loading attachment:', error);
          return of(null);
        })
      );
  }

  createPages() {
    this.pages = [];

    console.log('📖 Creating pages from', this.articles.length, 'articles');

    // Add cover page
    this.pages.push({
      pageNumber: 0,
      isDoublePage: false,
    });

    // Add article pages (2 articles per spread)
    for (let i = 0; i < this.articles.length; i += 2) {
      if (i + 1 < this.articles.length) {
        // Double page spread
        this.pages.push({
          pageNumber: this.pages.length,
          isDoublePage: true,
          leftArticle: this.articles[i],
          rightArticle: this.articles[i + 1],
        });
      } else {
        // Single page
        this.pages.push({
          pageNumber: this.pages.length,
          isDoublePage: false,
          article: this.articles[i],
        });
      }
    }

    // Add back cover
    this.pages.push({
      pageNumber: this.pages.length,
      isDoublePage: false,
    });

    console.log('📚 Total pages created:', this.pages.length);
  }

  nextPage() {
    if (this.currentPageIndex < this.pages.length - 1 && !this.isFlipping) {
      this.flipDirection = 'right';
      this.isFlipping = true;
      this.animatePageFlip(() => {
        this.currentPageIndex++;
        this.isFlipping = false;
      });
    }
  }

  previousPage() {
    if (this.currentPageIndex > 0 && !this.isFlipping) {
      this.flipDirection = 'left';
      this.isFlipping = true;
      this.animatePageFlip(() => {
        this.currentPageIndex--;
        this.isFlipping = false;
      });
    }
  }

  goToPage(index: number) {
    if (index >= 0 && index < this.pages.length && !this.isFlipping) {
      this.currentPageIndex = index;
      this.showThumbnails = false;
    }
  }

  animatePageFlip(callback: () => void) {
    setTimeout(() => {
      callback();
    }, 600);
  }

  zoomIn() {
    if (this.zoomLevel < 2) {
      this.zoomLevel += 0.25;
    }
  }

  zoomOut() {
    if (this.zoomLevel > 0.5) {
      this.zoomLevel -= 0.25;
    }
  }

  toggleFullscreen() {
    if (!this.isFullscreen) {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    this.isFullscreen = !this.isFullscreen;
  }

  toggleThumbnails() {
    this.showThumbnails = !this.showThumbnails;
  }

  closeViewer() {
    this.router.navigate(['/']);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowRight':
        this.nextPage();
        break;
      case 'ArrowLeft':
        this.previousPage();
        break;
      case 'Escape':
        if (this.showThumbnails) {
          this.showThumbnails = false;
        } else if (this.isFullscreen) {
          this.toggleFullscreen();
        } else {
          this.closeViewer();
        }
        break;
    }
  }

  get currentPage(): MagazinePage | undefined {
    return this.pages[this.currentPageIndex];
  }

  get canGoNext(): boolean {
    return this.currentPageIndex < this.pages.length - 1;
  }

  get canGoPrevious(): boolean {
    return this.currentPageIndex > 0;
  }

  get pageProgress(): number {
    return this.pages.length > 0
      ? ((this.currentPageIndex + 1) / this.pages.length) * 100
      : 0;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}